import React from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useAppDispatch } from 'src/hooks/store';

import {
  Tag as AntTag,
  Form as AntForm,
  Row,
  Col,
  Space as AntSpace,
  Input as AntInput,
  Button as AntButton,
  FormInstance,
} from 'antd';

import { Tag } from 'src/slices/tag';
import { createNewPost } from 'src/slices/post';
import { MetaData } from 'src/slices/metadata';
import { fetchCurrentTeamSettings, Team } from 'src/slices/auth';

import { openNotificationWithlogo } from 'src/containers/AddArticle';

const { CheckableTag } = AntTag;

const useStyles = createUseStyles({
  checkableTag: {
    fontSize: 'medium',
    padding: '2px 5px !important',
  },
});

interface AddArticleFormProps {
  defaultTag?: string;
  metaDatas: MetaData;
  tags: Tag[];
  selectedTags: Tag[];
  setSelectedTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  resetDrawer: () => void;
  closeDrawer: (e: any) => void;
  teamId: string | undefined;
  team: Team | null;
  formArticle: FormInstance;
}

export const AddArticleForm: React.VFC<
  AddArticleFormProps & React.HTMLAttributes<HTMLDivElement>
> = (props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const addArticle = ({ summary }: { summary: string }) => {
    const selectedTagsIds = props.tags
      .filter((tag) =>
        props.selectedTags.find((selectedTag) => tag._id === selectedTag._id),
      )
      .map((tag) => tag._id);

    const postNewArticle = async () => {
      try {
        await dispatch(
          createNewPost({
            microsoftTeamId: props.teamId || '',
            title: props.metaDatas.title,
            description: props.metaDatas.description,
            tags: selectedTagsIds,
            url: props.metaDatas.url,
            summary: summary,
            authorArticle: props.metaDatas.author,
            datePublished: props.metaDatas.date,
            image: props.metaDatas.image,
            logo: props.metaDatas.publisher,
            lang: props.metaDatas.lang,
          }),
        );
        if (props.teamId && !props.team) {
          dispatch(fetchCurrentTeamSettings(props.teamId ?? ''));
        }
        openNotificationWithlogo(
          'success',
          t('addArticleTranslation.successCreateNewArticle'),
          '',
        );
      } catch (error: any) {
        openNotificationWithlogo(
          'error',
          t('addArticleTranslation.errorCreateNewArticle'),
          t(`errors.${error?.message}`),
        );
      } finally {
        props.resetDrawer();
      }
    };

    postNewArticle();
  };

  const checkTag = (tag: Tag, checked: boolean) => {
    props.setSelectedTags(
      checked
        ? [...props.selectedTags, tag]
        : props.selectedTags.filter((currentTag) => currentTag !== tag),
    );
  };

  return (
    <AntForm
      layout="vertical"
      onFinish={addArticle}
      form={props.formArticle}
      fields={[
        {
          name: ['selectedTags'],
          value: props.defaultTag ? [props.defaultTag] : [],
        },
      ]}
    >
      <Row gutter={16}>
        <Col span={24}>
          <AntForm.Item
            name="selectedTags"
            label={t('addArticleTranslation.selectTags')}
            rules={[
              {
                required: props.selectedTags.length === 0,
                message: t('addArticleTranslation.errorSelectTags'),
              },
            ]}
          >
            <AntSpace size={[8, 10]} wrap className="tagsList">
              {props.tags.map((tag) => {
                const isChecked =
                  props.selectedTags.find(
                    (tagToFind) => tagToFind._id === tag._id,
                  ) !== undefined;

                return (
                  <CheckableTag
                    key={tag._id}
                    checked={isChecked}
                    onChange={(checked) => checkTag(tag, checked)}
                    className={`${isChecked ? '' : 'ant-tag-processing'} ${
                      classes.checkableTag
                    }`}
                  >
                    {`#${tag.name}`}
                  </CheckableTag>
                );
              })}
            </AntSpace>
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <AntForm.Item
            name="summary"
            label={t('addArticleTranslation.summaryText')}
            rules={[
              {
                message: t('addArticleTranslation.summaryInput'),
              },
            ]}
          >
            <AntInput.TextArea
              rows={4}
              placeholder={t('addArticleTranslation.summaryInput')}
            />
          </AntForm.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <AntSpace>
            <AntButton onClick={(e) => props.closeDrawer(e)}>
              {t('common.cancelBtn')}
            </AntButton>
            <AntButton type="primary" htmlType="submit">
              {t('common.addBtn')}
            </AntButton>
          </AntSpace>
        </Col>
      </Row>
    </AntForm>
  );
};
